import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import FolderIcon from "@mui/icons-material/Folder";
import { DOCUMENT_ACTION } from "../../redux/action";
import {
  categoryForMoveFetch,
  filedCopiesPurposeTabForCategory,
  isFiledCopiesPurposeTab,
  K1_PACKAGE_TAB_LABEL,
  normalizeDocumentCategory,
  purposeTabFromDocType,
  resolveStaffDocTypeFromPurpose,
} from "../../utils/moveDocumentScope";
import "./move-item-modal.scss";

const BASE_PURPOSE_TABS = [
  { id: "sent", label: "Sent" },
  { id: "received", label: "Received" },
  { id: "office", label: "Office" },
];

/**
 * Folder picker for moving a file or folder. Supports Individual / Business
 * categories and Sent / Received / Office purpose tabs; only folders matching
 * the selected scope are listed.
 */
const MoveItemModal = ({
  open,
  onClose,
  item,
  userId,
  homeFolder,
  currentFolderId,
  onConfirm,
  initialDocumentCategory = "individual",
  initialPurposeTab = "received",
  showBusinessTab = false,
  clientBusinessId = null,
  lockBusinessCategory = false,
}) => {
  const dispatch = useDispatch();

  const initialPath = useMemo(
    () => [{ id: 0, name: "Home", created_by: 0 }],
    [],
  );

  const [categoryTab, setCategoryTab] = useState("individual");
  const [purposeTab, setPurposeTab] = useState("received");
  const [path, setPath] = useState(initialPath);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const filedCopiesPurposeTab = useMemo(
    () => filedCopiesPurposeTabForCategory(categoryTab),
    [categoryTab],
  );

  const purposeTabs = useMemo(() => {
    return [
      ...BASE_PURPOSE_TABS,
      { id: filedCopiesPurposeTab, label: K1_PACKAGE_TAB_LABEL },
    ];
  }, [filedCopiesPurposeTab]);

  const activeDocType = resolveStaffDocTypeFromPurpose(purposeTab);
  const activeDocumentCategory = categoryForMoveFetch(purposeTab, categoryTab);

  const current = path[path.length - 1] || initialPath[0];
  const isAtRoot = current.id === 0;

  const sourceFolderId =
    item?.type === "folder"
      ? item?.data?.client_folder_id ?? item?.data?.folder_id
      : null;

  const loadFolders = useCallback(
    async (folderId, folderName, docType, documentCategory) => {
      if (!userId) {
        setFolders([]);
        return;
      }
      setLoading(true);
      try {
        const response = await dispatch(
          DOCUMENT_ACTION.getUserFolders({
            user_id: userId,
            folder_name: folderId === 0 ? homeFolder : folderName,
            folder_id: folderId,
            folder_type: docType,
            ...(documentCategory
              ? { document_category: documentCategory }
              : {}),
            ...(documentCategory === "business" &&
            clientBusinessId != null &&
            clientBusinessId !== ""
              ? { client_business_id: clientBusinessId }
              : {}),
          }),
        );
        const payload = Array.isArray(response?.payload) ? response.payload : [];
        const filtered = payload.filter((f) => {
          const id = f.client_folder_id ?? f.folder_id;
          if (sourceFolderId && id === sourceFolderId) return false;
          const folderType = f.folder_type || f.doc_type;
          if (folderType && folderType !== docType) return false;
          if (documentCategory) {
            const cat = normalizeDocumentCategory(f.document_category);
            if (cat !== documentCategory) return false;
          }
          return true;
        });
        setFolders(filtered);
      } catch (e) {
        setFolders([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, userId, homeFolder, sourceFolderId, clientBusinessId],
  );

  const resetToRoot = useCallback(
    (docType, documentCategory) => {
      setPath(initialPath);
      loadFolders(0, homeFolder, docType, documentCategory);
    },
    [initialPath, homeFolder, loadFolders],
  );

  useEffect(() => {
    if (!open) {
      setFolders([]);
      setSubmitting(false);
      return;
    }

    const itemDocType =
      item?.type === "folder"
        ? item?.data?.folder_type
        : item?.data?.doc_type;
    const itemCategory = lockBusinessCategory
      ? "business"
      : normalizeDocumentCategory(
          initialDocumentCategory || item?.data?.document_category,
        );
    const itemPurpose =
      initialPurposeTab ||
      purposeTabFromDocType(itemDocType) ||
      "received";

    setCategoryTab(itemCategory);
    setPurposeTab(itemPurpose);
    const docType = resolveStaffDocTypeFromPurpose(itemPurpose);
    const documentCategory = categoryForMoveFetch(itemPurpose, itemCategory);
    resetToRoot(docType, documentCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, initialDocumentCategory, initialPurposeTab]);

  const handleCategoryChange = (nextCategory) => {
    let nextPurpose = purposeTab;
    if (isFiledCopiesPurposeTab(purposeTab)) {
      nextPurpose = filedCopiesPurposeTabForCategory(nextCategory);
      setPurposeTab(nextPurpose);
    }
    setCategoryTab(nextCategory);
    const documentCategory = categoryForMoveFetch(nextPurpose, nextCategory);
    resetToRoot(
      resolveStaffDocTypeFromPurpose(nextPurpose),
      documentCategory,
    );
  };

  const handlePurposeChange = (nextPurpose) => {
    setPurposeTab(nextPurpose);
    const docType = resolveStaffDocTypeFromPurpose(nextPurpose);
    const documentCategory = categoryForMoveFetch(nextPurpose, categoryTab);
    resetToRoot(docType, documentCategory);
  };

  const handleEnterFolder = (folder) => {
    const id = folder.client_folder_id ?? folder.folder_id;
    if (!id) return;
    const nextPath = [
      ...path,
      {
        id,
        name: folder.folder_name,
        created_by: folder.created_by ?? 0,
        folder_type: folder.folder_type,
      },
    ];
    setPath(nextPath);
    loadFolders(id, folder.folder_name, activeDocType, activeDocumentCategory);
  };

  const handleCrumbClick = (index) => {
    const nextPath = path.slice(0, index + 1);
    setPath(nextPath);
    const target = nextPath[nextPath.length - 1];
    loadFolders(target.id, target.name, activeDocType, activeDocumentCategory);
  };

  const handleConfirm = async () => {
    if (!onConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm({
        destination: {
          id: current.id,
          name: current.id === 0 ? homeFolder : current.name,
          navPath: path,
        },
        documentCategory: activeDocumentCategory,
        docType: activeDocType,
        purposeTab,
        categoryTab: normalizeDocumentCategory(categoryTab),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const headerName =
    item?.displayName ||
    (item?.type === "folder"
      ? item?.data?.folder_name
      : item?.data?.document_name) ||
    "item";

  const scopeLabel = [
    isFiledCopiesPurposeTab(purposeTab)
      ? K1_PACKAGE_TAB_LABEL
      : purposeTab === "office"
        ? "Office"
        : categoryTab === "business"
          ? "Business"
          : "Individual",
    purposeTabs.find((t) => t.id === purposeTab)?.label,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog
      open={Boolean(open)}
      onClose={submitting ? undefined : onClose}
      classes={{ paper: "move-item-modal-paper" }}
      fullWidth
      maxWidth="sm"
    >
      <div className="move-item-modal">
        <div className="move-item-modal-header">
          <h2 className="move-item-modal-title">Move "{headerName}"</h2>
          <button
            type="button"
            className="move-item-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <div className="move-item-modal-tabs">
          <div className="move-item-category-tabs">
            <button
              type="button"
              className={`move-item-category-tab ${
                categoryTab === "individual" ? "is-active" : ""
              }`}
              onClick={() => {
                if (!lockBusinessCategory) handleCategoryChange("individual");
              }}
              disabled={submitting || lockBusinessCategory}
            >
              Individual
            </button>
            {showBusinessTab && (
              <button
                type="button"
                className={`move-item-category-tab ${
                  categoryTab === "business" ? "is-active" : ""
                }`}
                onClick={() => {
                  if (!lockBusinessCategory) handleCategoryChange("business");
                }}
                disabled={submitting || lockBusinessCategory}
              >
                Business
              </button>
            )}
          </div>
          <div className="move-item-purpose-tabs">
            {purposeTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`move-item-purpose-tab ${
                  purposeTab === tab.id ? "is-active" : ""
                }`}
                onClick={() => handlePurposeChange(tab.id)}
                disabled={submitting}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <p className="move-item-scope-hint">
            Showing folders in <strong>{scopeLabel}</strong>
          </p>
        </div>

        <div className="move-item-modal-crumbs">
          {path.map((crumb, idx) => (
            <React.Fragment key={`mv-crumb-${idx}`}>
              {idx > 0 && <span className="move-item-sep">›</span>}
              <button
                type="button"
                className={`move-item-crumb ${
                  idx === path.length - 1 ? "is-current" : ""
                }`}
                onClick={() => handleCrumbClick(idx)}
                disabled={submitting}
              >
                {idx === 0 ? homeFolder || "Home" : crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        <div className="move-item-modal-body">
          {loading ? (
            <div className="move-item-modal-loader">
              <CircularProgress size={24} />
            </div>
          ) : folders.length === 0 ? (
            <div className="move-item-modal-empty">
              No subfolders here for this scope. Use "Move here" to place the
              item in this location.
            </div>
          ) : (
            <ul className="move-item-modal-list">
              {folders.map((folder) => {
                const id = folder.client_folder_id ?? folder.folder_id;
                return (
                  <li key={`mv-folder-${id}`}>
                    <button
                      type="button"
                      className="move-item-folder-row"
                      onClick={() => handleEnterFolder(folder)}
                      disabled={submitting}
                    >
                      <FolderIcon fontSize="small" className="move-item-icon" />
                      <span className="move-item-folder-name">
                        {folder.folder_name}
                      </span>
                      <span className="move-item-folder-enter">›</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="move-item-modal-actions">
          <button
            type="button"
            className="move-item-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="move-item-btn-primary"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting
              ? "Moving…"
              : `Move here${isAtRoot ? "" : ` (${current.name})`}`}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default MoveItemModal;
